import { isIrrelevantScopeAccount, accountScopeState } from "./accountScope";
import { prepareExpDateField } from "./datePicker";
import {
  ensureLedgerMarkIndex,
  markForAccountId,
  type LedgerDisplayMark,
} from "./ledgerMarks";
import {
  loadLedgerCatalog,
  type LedgerAccount,
  type LedgerBroker,
} from "./ownLedgerClient";
import { showToast } from "./toast";
import {
  afterWheelLedgerChange,
  createLedgerTransaction,
  etTodayISO,
  readExpDateField,
  WheelActionError,
  type LedgerTransactionKind,
} from "./wheelActions";

const MODAL_ID = "sfe-history-txn-add-modal";

const OPTION_ACTIONS: { value: string; label: string; kind: LedgerTransactionKind }[] = [
  { value: "sell", label: "Sell", kind: "sto" },
  { value: "buy", label: "Buy", kind: "btc" },
  { value: "buy_to_open", label: "Buy to open", kind: "bto" },
  { value: "sell_to_close", label: "Sell to close", kind: "stc" },
  { value: "expired", label: "Expired worthless", kind: "exp" },
  { value: "assigned", label: "Assigned", kind: "asgn" },
];

const STOCK_ACTIONS: { value: string; label: string; kind: LedgerTransactionKind }[] = [
  { value: "buy", label: "Buy", kind: "stock_buy" },
  { value: "sell", label: "Sell", kind: "stock_sell" },
];

export interface HistoryTransactionAddDefaults {
  instrument?: "option" | "stock";
  kind?: LedgerTransactionKind;
  ticker?: string;
  side?: "put" | "call";
  strike?: number;
  expDate?: string;
  accountId?: string;
}

type AssignableCatalog = {
  brokers: LedgerBroker[];
  accounts: LedgerAccount[];
};

/** Prefill add-transaction defaults from an existing history row. */
export function historyTransactionAddDefaultsFromTxn(txn: {
  instrument_type?: string;
  ticker?: string;
  side?: string;
  strike?: number;
  exp_date?: string;
  account_id?: string | null;
}): HistoryTransactionAddDefaults {
  const instrument = txn.instrument_type === "stock" ? "stock" : "option";
  return {
    instrument,
    kind: instrument === "stock" ? "stock_buy" : "sto",
    ticker: txn.ticker,
    side: txn.side === "call" ? "call" : txn.side === "put" ? "put" : undefined,
    strike: txn.strike != null && Number.isFinite(txn.strike) ? Number(txn.strike) : undefined,
    expDate: txn.exp_date ? String(txn.exp_date).slice(0, 10) : undefined,
    accountId: txn.account_id ? String(txn.account_id).trim() : undefined,
  };
}

function closeModal(): void {
  document.getElementById(MODAL_ID)?.classList.add("hidden");
}

function hint(text: string): string {
  return `<span class="hint" tabindex="0" role="img" data-tip="${text.replaceAll('"', "&quot;")}">i</span>`;
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function isHiddenAccountId(accountId: string | null | undefined): boolean {
  const id = String(accountId ?? "")
    .trim()
    .toLowerCase();
  return !id || id === "undefined" || id === "undefined:undefined" || id.startsWith("undefined:");
}

function brokerChoiceLabel(broker: LedgerBroker): string {
  return broker.name || broker.broker_id || "Broker";
}

function accountChoiceLabel(account: LedgerAccount, mark: LedgerDisplayMark | null): string {
  return (
    mark?.accountShort ||
    account.short_name ||
    account.label ||
    account.name ||
    account.account_id
  );
}

async function loadAssignableCatalog(): Promise<AssignableCatalog> {
  await ensureLedgerMarkIndex(true);
  const catalog = await loadLedgerCatalog(true);
  const accounts = catalog.accounts
    .filter((account) => !isIrrelevantScopeAccount(account) && !isHiddenAccountId(account.account_id))
    .sort((a, b) => {
      const aLabel = accountChoiceLabel(a, markForAccountId(a.account_id));
      const bLabel = accountChoiceLabel(b, markForAccountId(b.account_id));
      return aLabel.localeCompare(bLabel, undefined, { sensitivity: "base" });
    });
  const brokerIds = new Set(accounts.map((account) => account.broker_id));
  const brokers = catalog.brokers
    .filter((broker) => brokerIds.has(broker.broker_id) && !isHiddenAccountId(broker.broker_id))
    .sort((a, b) =>
      brokerChoiceLabel(a).localeCompare(brokerChoiceLabel(b), undefined, { sensitivity: "base" }),
    );
  return { brokers, accounts };
}

function pickDefaultAccountId(
  accounts: LedgerAccount[],
  preferred?: string | null,
): string {
  const preferredId = String(preferred ?? "").trim();
  if (preferredId && accounts.some((account) => account.account_id === preferredId)) {
    return preferredId;
  }
  const scoped = accountScopeState().accountIds.filter((id) =>
    accounts.some((account) => account.account_id === id),
  );
  if (scoped.length) return scoped[0]!;
  return accounts[0]?.account_id ?? "";
}

function kindToActionValue(
  instrument: "option" | "stock",
  kind: LedgerTransactionKind,
): string {
  const actions = instrument === "stock" ? STOCK_ACTIONS : OPTION_ACTIONS;
  return actions.find((row) => row.kind === kind)?.value ?? actions[0]!.value;
}

function actionValueToKind(
  instrument: "option" | "stock",
  action: string,
): LedgerTransactionKind {
  const actions = instrument === "stock" ? STOCK_ACTIONS : OPTION_ACTIONS;
  return actions.find((row) => row.value === action)?.kind ?? (instrument === "stock" ? "stock_buy" : "sto");
}

function populateActionSelect(
  select: HTMLSelectElement,
  instrument: "option" | "stock",
  selected: string,
): void {
  const actions = instrument === "stock" ? STOCK_ACTIONS : OPTION_ACTIONS;
  select.innerHTML = actions.map((row) => `<option value="${row.value}">${row.label}</option>`).join("");
  if (actions.some((row) => row.value === selected)) {
    select.value = selected;
  } else {
    select.value = actions[0]?.value ?? "sell";
  }
}

function populateBrokerSelect(
  select: HTMLSelectElement,
  brokers: LedgerBroker[],
  selectedBrokerId: string,
): void {
  if (!brokers.length) {
    select.innerHTML = `<option value="">No brokers</option>`;
    select.value = "";
    select.disabled = true;
    return;
  }
  select.disabled = false;
  select.innerHTML = brokers
    .map((broker) => {
      const selected = broker.broker_id === selectedBrokerId ? " selected" : "";
      return `<option value="${esc(broker.broker_id)}"${selected}>${esc(brokerChoiceLabel(broker))}</option>`;
    })
    .join("");
  if (brokers.some((broker) => broker.broker_id === selectedBrokerId)) {
    select.value = selectedBrokerId;
  } else {
    select.value = brokers[0]!.broker_id;
  }
}

function populateAccountSelect(
  select: HTMLSelectElement,
  accounts: LedgerAccount[],
  brokerId: string,
  selectedAccountId: string,
): void {
  const forBroker = accounts.filter((account) => account.broker_id === brokerId);
  if (!forBroker.length) {
    select.innerHTML = `<option value="">No accounts</option>`;
    select.value = "";
    select.disabled = true;
    return;
  }
  select.disabled = false;
  select.innerHTML = forBroker
    .map((account) => {
      const mark = markForAccountId(account.account_id);
      const selected = account.account_id === selectedAccountId ? " selected" : "";
      return `<option value="${esc(account.account_id)}"${selected}>${esc(accountChoiceLabel(account, mark))}</option>`;
    })
    .join("");
  if (forBroker.some((account) => account.account_id === selectedAccountId)) {
    select.value = selectedAccountId;
  } else {
    select.value = forBroker[0]!.account_id;
  }
}

function syncAddForm(
  form: HTMLFormElement,
  instrument: "option" | "stock",
  kind: LedgerTransactionKind,
): void {
  const optionSideField = form.querySelector<HTMLElement>("[data-history-txn-side-field]");
  const optionStrikeField = form.querySelector<HTMLElement>("[data-history-txn-strike-field]");
  const optionExpField = form.querySelector<HTMLElement>("[data-history-txn-exp-field]");
  const priceWrap = form.querySelector<HTMLElement>("[data-history-txn-price-wrap]");
  const formNote = form.querySelector<HTMLElement>("[data-history-txn-form-note]");
  const priceInput = form.elements.namedItem("avg_price") as HTMLInputElement;
  const strikeInput = form.elements.namedItem("strike") as HTMLInputElement;

  const isOption = instrument === "option";
  if (optionSideField) optionSideField.hidden = !isOption;
  if (optionStrikeField) optionStrikeField.hidden = !isOption;
  if (optionExpField) optionExpField.hidden = !isOption;

  if (formNote) {
    formNote.textContent = isOption
      ? "Quantity = number of contracts. Price = premium per share."
      : "Quantity = number of shares. Price = price per share.";
  }

  const priceLabel = form.querySelector("[data-history-txn-price-label]");
  if (priceLabel) {
    if (!isOption) {
      priceLabel.textContent = "Price per share";
    } else if (kind === "bto" || kind === "btc") {
      priceLabel.textContent = "Premium paid per share";
    } else if (kind === "exp") {
      priceLabel.textContent = "Premium (expired = $0)";
    } else if (kind === "asgn") {
      priceLabel.textContent = "Assignment price (strike)";
    } else {
      priceLabel.textContent = "Premium per share";
    }
  }

  const needsPrice = kind !== "exp" && kind !== "asgn";
  if (priceWrap) priceWrap.hidden = !needsPrice;
  priceInput.required = needsPrice;
  priceInput.disabled = !needsPrice;

  if (kind === "exp") {
    priceInput.value = "0";
  } else if (kind === "asgn") {
    const strike = Number(strikeInput.value);
    priceInput.value = strike > 0 ? String(strike) : "";
  }

  strikeInput.required = isOption;
  const expInput = form.elements.namedItem("exp_date") as HTMLInputElement;
  expInput.required = isOption;
}

/**
 * Add a ledger transaction (Stock Forge+ modal → POST /api/v1/wheel/transactions).
 */
export function openHistoryTransactionAddModal(
  options: {
    onSaved?: () => void | Promise<void>;
    defaults?: HistoryTransactionAddDefaults;
  } = {},
): void {
  const defaults = options.defaults ?? {};
  let overlay = document.getElementById(MODAL_ID);

  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = MODAL_ID;
    overlay.className = "modal hidden sfe-history-txn-add-modal";
    overlay.innerHTML = `
      <div class="modal-card modal-card--wide" role="dialog" aria-labelledby="sfe-history-txn-add-title">
        <div class="row">
          <h3 id="sfe-history-txn-add-title">Add transaction</h3>
        </div>
        <button type="button" class="modal-close" data-action="history-txn-add-close" aria-label="Close">✕</button>
        <form class="grid cols-2 sfe-history-txn-add-form" data-history-txn-add-form>
          <div class="field">
            <label>Instrument ${hint("Option for puts/calls; Stock for share buys and sells.")}</label>
            <select name="instrument" required>
              <option value="option">Option</option>
              <option value="stock">Stock</option>
            </select>
          </div>
          <div class="field">
            <label>Action ${hint("Sell/Buy for opens and closes; Expired/Assigned for option outcomes.")}</label>
            <select name="action" required data-history-txn-action-select></select>
          </div>
          <div class="field">
            <label>Broker ${hint("Ledger broker this trade belongs to.")}</label>
            <select name="broker" required data-history-txn-broker-select>
              <option value="">Loading…</option>
            </select>
          </div>
          <div class="field">
            <label>Account ${hint("Ledger account under the selected broker.")}</label>
            <select name="account" required data-history-txn-account-select>
              <option value="">Loading…</option>
            </select>
          </div>
          <div class="field">
            <label>Ticker ${hint("Underlying stock symbol.")}</label>
            <input type="text" name="ticker" required autocomplete="off" spellcheck="false" maxlength="12" />
          </div>
          <div class="field">
            <label>Trade date ${hint("Date the trade settled.")}</label>
            <input type="text" name="trade_date" class="sfe-focus-exp-input" placeholder="Select date" inputmode="numeric" autocomplete="off" spellcheck="false" maxlength="10" required />
          </div>
          <div class="field">
            <label>Quantity ${hint("Contracts for options; shares for stock.")}</label>
            <input type="number" name="quantity" min="1" step="1" required />
          </div>
          <div class="field" data-history-txn-price-wrap>
            <label><span data-history-txn-price-label>Premium per share</span> ${hint("Option premium per share (not per contract). Stock price per share.")}</label>
            <input type="number" name="avg_price" min="0" step="0.01" />
          </div>
          <div class="field" data-history-txn-side-field>
            <label>Side ${hint("Put or call.")}</label>
            <select name="side">
              <option value="put">Put</option>
              <option value="call">Call</option>
            </select>
          </div>
          <div class="field" data-history-txn-strike-field>
            <label>Strike ${hint("Option strike price.")}</label>
            <input type="number" name="strike" min="0" step="0.01" />
          </div>
          <div class="field field-span-2" data-history-txn-exp-field>
            <label>Expiration ${hint("Option expiration date.")}</label>
            <input type="text" name="exp_date" class="sfe-focus-exp-input" placeholder="Select date" inputmode="numeric" autocomplete="off" spellcheck="false" maxlength="10" />
          </div>
          <p class="muted small field-span-2 sfe-history-txn-form-note" data-history-txn-form-note></p>
          <div class="row field-span-2 sfe-history-txn-add-foot">
            <span class="spacer"></span>
            <button type="button" class="ghost small" data-action="history-txn-add-cancel">Cancel</button>
            <button type="submit" class="small">Save</button>
          </div>
        </form>
      </div>`;
    document.body.appendChild(overlay);

    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        closeModal();
        return;
      }
      const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
      if (action === "history-txn-add-close" || action === "history-txn-add-cancel") {
        closeModal();
      }
    });
  }

  const form = overlay.querySelector<HTMLFormElement>("[data-history-txn-add-form]");
  if (!form) return;

  const instrumentSelect = form.elements.namedItem("instrument") as HTMLSelectElement;
  const actionSelect = form.querySelector<HTMLSelectElement>("[data-history-txn-action-select]")!;
  const brokerSelect = form.querySelector<HTMLSelectElement>("[data-history-txn-broker-select]")!;
  const accountSelect = form.querySelector<HTMLSelectElement>("[data-history-txn-account-select]")!;
  const strikeInput = form.elements.namedItem("strike") as HTMLInputElement;
  const tradeDateInput = form.elements.namedItem("trade_date") as HTMLInputElement;
  const expInput = form.elements.namedItem("exp_date") as HTMLInputElement;
  const submit = form.querySelector<HTMLButtonElement>('[type="submit"]');

  let catalog: AssignableCatalog = { brokers: [], accounts: [] };

  const readInstrument = (): "option" | "stock" =>
    instrumentSelect.value === "stock" ? "stock" : "option";

  const readKind = (): LedgerTransactionKind =>
    actionValueToKind(readInstrument(), actionSelect.value);

  const refreshForm = (): void => {
    syncAddForm(form, readInstrument(), readKind());
  };

  const syncAccountOptions = (preferredAccountId?: string): void => {
    const brokerId = brokerSelect.value;
    const preferred =
      preferredAccountId ||
      (accountSelect.value &&
      catalog.accounts.some(
        (account) => account.account_id === accountSelect.value && account.broker_id === brokerId,
      )
        ? accountSelect.value
        : "");
    populateAccountSelect(accountSelect, catalog.accounts, brokerId, preferred);
  };

  instrumentSelect.onchange = () => {
    const instrument = readInstrument();
    populateActionSelect(
      actionSelect,
      instrument,
      instrument === "stock" ? "buy" : "sell",
    );
    refreshForm();
  };

  actionSelect.onchange = refreshForm;
  brokerSelect.onchange = () => syncAccountOptions();
  strikeInput.oninput = () => {
    if (readKind() === "asgn") refreshForm();
  };

  const instrument = defaults.instrument ?? "option";
  instrumentSelect.value = instrument;
  const initialKind =
    defaults.kind ?? (instrument === "stock" ? "stock_buy" : "sto");
  populateActionSelect(
    actionSelect,
    instrument,
    kindToActionValue(instrument, initialKind),
  );

  (form.elements.namedItem("ticker") as HTMLInputElement).value = (defaults.ticker ?? "")
    .trim()
    .toUpperCase();
  (form.elements.namedItem("quantity") as HTMLInputElement).value = "1";
  (form.elements.namedItem("avg_price") as HTMLInputElement).value = "";
  strikeInput.value =
    defaults.strike != null && Number.isFinite(defaults.strike) ? String(defaults.strike) : "";
  (form.elements.namedItem("side") as HTMLSelectElement).value =
    defaults.side === "call" ? "call" : "put";

  brokerSelect.innerHTML = `<option value="">Loading…</option>`;
  accountSelect.innerHTML = `<option value="">Loading…</option>`;
  brokerSelect.disabled = true;
  accountSelect.disabled = true;
  if (submit) submit.disabled = true;

  prepareExpDateField(tradeDateInput, etTodayISO(), {
    onInteract: () => {
      /* keep modal open while picking trade date */
    },
  });
  prepareExpDateField(expInput, defaults.expDate ?? "", {
    onInteract: () => {
      /* keep modal open while picking expiration */
    },
  });
  refreshForm();

  form.onsubmit = (event) => {
    event.preventDefault();
    if (submit) submit.disabled = true;
    void (async () => {
      try {
        const kind = readKind();
        const instrumentType = readInstrument();
        const ticker = (form.elements.namedItem("ticker") as HTMLInputElement).value;
        const tradeDate = readExpDateField(tradeDateInput);
        const quantity = Number((form.elements.namedItem("quantity") as HTMLInputElement).value);
        const avgPriceRaw = (form.elements.namedItem("avg_price") as HTMLInputElement).value;
        const avgPrice = avgPriceRaw === "" ? undefined : Number(avgPriceRaw);
        const accountId = accountSelect.value.trim();

        await createLedgerTransaction({
          kind,
          ticker,
          tradeDate,
          quantity,
          avgPrice,
          accountId,
          side:
            instrumentType === "option"
              ? ((form.elements.namedItem("side") as HTMLSelectElement).value as "put" | "call")
              : undefined,
          strike: instrumentType === "option" ? Number(strikeInput.value) : undefined,
          expDate: instrumentType === "option" ? readExpDateField(expInput) : undefined,
        });
        showToast("Transaction added.", "success");
        closeModal();
        await afterWheelLedgerChange(options.onSaved ?? undefined);
      } catch (err) {
        showToast(err instanceof WheelActionError ? err.message : "Could not add transaction.", "error");
      } finally {
        if (submit) submit.disabled = !catalog.accounts.length;
      }
    })();
  };

  overlay.classList.remove("hidden");
  (form.elements.namedItem("ticker") as HTMLInputElement).focus();

  void (async () => {
    try {
      catalog = await loadAssignableCatalog();
      if (!catalog.accounts.length) {
        brokerSelect.innerHTML = `<option value="">No brokers</option>`;
        accountSelect.innerHTML = `<option value="">No accounts</option>`;
        showToast("No ledger accounts available. Add a broker account in Settings first.", "error");
        return;
      }
      const defaultAccountId = pickDefaultAccountId(catalog.accounts, defaults.accountId);
      const defaultAccount =
        catalog.accounts.find((account) => account.account_id === defaultAccountId) ??
        catalog.accounts[0]!;
      populateBrokerSelect(brokerSelect, catalog.brokers, defaultAccount.broker_id);
      syncAccountOptions(defaultAccount.account_id);
      if (submit) submit.disabled = false;
    } catch (err) {
      brokerSelect.innerHTML = `<option value="">Failed to load</option>`;
      accountSelect.innerHTML = `<option value="">Failed to load</option>`;
      showToast(err instanceof Error ? err.message : "Could not load accounts.", "error");
    }
  })();
}
